(function() {
  'use strict';

  const SELECTORS = {
    container: '[data-multipack-selector]',
    hiddenQuantity: '[data-multipack-quantity]',
    hiddenPackId: '[data-multipack-pack-id]',
    hiddenPackQuantity: '[data-multipack-pack-quantity]',
    hiddenPackLabel: '[data-multipack-pack-label]',
    hiddenPackImage: '[data-multipack-pack-image]',
    hiddenPackImageFile: '[data-multipack-pack-image-file]',
    hiddenPackDisplayLabel: '[data-multipack-pack-display-label]',
    productPrice: '[data-product-price]',
    comparePrice: '[data-compare-price]',
    comparePriceWrap: '[data-product-price-wrap]',
    priceA11y: '[data-a11y-price]',
    comparePriceA11y: '[data-compare-price-a11y]',
    piecePrice: '[data-piece-price]',
    piecePriceWrapper: '[data-piece-price-wrapper]',
    productSection: '[data-section-type="product"]',
    masterSelect: '[data-product-select]',
    variantInput: '[data-variant-input]',
    quantityBlock: '.product__quantity'
  };

  class MultipackSelector {
    constructor(container) {
      this.container = container;
      this.productSection = container.closest(SELECTORS.productSection);
      if (!this.productSection) return;

      this.hiddenQuantity = container.querySelector(SELECTORS.hiddenQuantity);
      this.hiddenPackId = container.querySelector(SELECTORS.hiddenPackId);
      this.hiddenPackQuantity = container.querySelector(SELECTORS.hiddenPackQuantity);
      this.hiddenPackLabel = container.querySelector(SELECTORS.hiddenPackLabel);
      this.hiddenPackImage = container.querySelector(SELECTORS.hiddenPackImage);
      this.hiddenPackImageFile = container.querySelector(SELECTORS.hiddenPackImageFile);
      this.hiddenPackDisplayLabel = container.querySelector(SELECTORS.hiddenPackDisplayLabel);

      try {
        this.packs = JSON.parse(container.dataset.packs);
      } catch (e) {
        return;
      }

      if (!this.packs || !this.packs.length) return;

      this.masterSelect = this.productSection.querySelector(SELECTORS.masterSelect);

      // Load all variants from the product JSON
      const variantJsonEl = this.productSection.querySelector('[data-variant-json]');
      try {
        this.variants = variantJsonEl ? JSON.parse(variantJsonEl.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      // Build O(1) variant lookup map keyed by string ID
      this.variantMap = {};
      for (const v of this.variants) {
        this.variantMap[String(v.id)] = v;
      }

      // Determine size option axis and build variant→pack mapping ONCE
      this._detectSizeAxis();

      // Determine the true base (qty=1) variant
      this.resolveBaseVariant();

      // Translation labels from Liquid
      this.labelOneTime = container.dataset.labelOneTime || '';
      this.labelSubscription = container.dataset.labelSubscription || '';

      this._lastAppliedPack = null;
      this._sealListenersAttached = false;

      this.init();
    }

    init() {
      this.productSection.addEventListener('variantChange', (evt) => this.onVariantChange(evt));
      this.disableCompetingQuantityInputs();
      this._cachePriceElements();
      this.storeOriginalPiecePrice();
      this._initPriceGuard();
      this.applyPackFromCurrentVariant();
      this.handleLegacyVariantParam();
      this.handlePackQueryParam();
      this.checkCartConflicts();
      this._registerFormSafeguards();

      // Poll for Seal widget — it renders asynchronously after page load
      this.initSealIntegration();
    }

    /**
     * Re-sync pack properties on every add-to-cart attempt (not only when Seal
     * has mounted — avoids stale Einzelpackung / empty display label on fast ATC).
     *
     * Three layers of defence:
     * 1. submit (capture) — fires for native form submits before Seal intercepts.
     * 2. ATC button click — early sync so Seal reads fresh values.
     * 3. formdata event — fires synchronously inside new FormData(form) regardless
     *    of who triggers it (Seal, theme JS, or native submit). Runs last, so it
     *    wins over any quantity/property clobber Seal may have done when switching
     *    between one-time and subscription modes.
     */
    _registerFormSafeguards() {
      const form = this.productSection.querySelector('form[action*="/cart/add"]');
      if (form) {
        form.addEventListener('submit', () => this._ensureBaseVariantAtSubmit(), true);
        form.addEventListener('formdata', (e) => {
          const pack = this._getCurrentPack();
          if (!pack) return;
          e.formData.set('quantity', String(pack.quantity));
          e.formData.set('properties[_ed_pack_quantity]', String(pack.quantity));
          e.formData.set('properties[_ed_pack_id]', pack.id);
          e.formData.set('properties[_ed_pack_label]', pack.label);
          e.formData.set('properties[_ed_pack_display_label]', this._buildDisplayLabel(pack));
          if (this.baseVariantId) e.formData.set('id', this.baseVariantId);
          const targetPlanId = this._resolveSellingPlanForCurrentPack();
          if (targetPlanId) e.formData.set('selling_plan', String(targetPlanId));
        }, true);
      }
      const atcBtn = this.productSection.querySelector('[data-add-to-cart]');
      if (atcBtn) {
        atcBtn.addEventListener('click', () => this._ensureBaseVariantAtSubmit());
      }
    }

    _cachePriceElements() {
      this._priceEl = this.productSection.querySelector(SELECTORS.productPrice);
      this._comparePriceWrap = this.productSection.querySelector(SELECTORS.comparePriceWrap);
      this._comparePriceEl = this.productSection.querySelector(SELECTORS.comparePrice);
      this._priceA11y = this.productSection.querySelector(SELECTORS.priceA11y);
      this._comparePriceA11y = this.productSection.querySelector(SELECTORS.comparePriceA11y);
      this._piecePriceWrapper = this.productSection.querySelector(SELECTORS.piecePriceWrapper);
      this._piecePriceEl = this.productSection.querySelector(SELECTORS.piecePrice);
    }

    /* ================================================================
     *  SHARED HELPERS
     * ================================================================ */

    _findVariantById(id) {
      return this.variantMap[String(id)] || null;
    }

    _getFlavorLabel() {
      const baseVariant = this._findVariantById(this.baseVariantId);
      if (!baseVariant) return '';
      const axes = ['option1', 'option2', 'option3'];
      return axes
        .filter(a => a !== this.sizeAxis)
        .map(a => baseVariant[a])
        .filter(v => v && v !== 'Default Title')
        .join(' / ');
    }

    _buildDisplayLabel(pack) {
      const pricePerUnit = this._packField(pack, 'price_per_unit');
      const discountDisplay = this._packField(pack, 'discount_display');
      const pricePart = pricePerUnit > 0
        ? ` (${theme.Currency.formatMoney(pricePerUnit, theme.settings.moneyFormat)} / Stück)`
        : '';
      const discountPart = discountDisplay ? ` – ${discountDisplay}` : '';
      const flavor = this._getFlavorLabel();
      const flavorPart = flavor ? ` / ${flavor}` : '';
      return `${pack.label}${pricePart}${discountPart}${flavorPart}`;
    }

    /**
     * Single source of truth for line item properties sent to checkout.
     * Call on every pack selection and immediately before add-to-cart so labels
     * never reflect a stale single-pack / catalog default.
     */
    _syncPackLineProperties(pack) {
      if (!pack) return;
      this.hiddenQuantity.value = pack.quantity;
      this.hiddenPackId.value = pack.id;
      this.hiddenPackQuantity.value = pack.quantity;
      if (this.hiddenPackLabel) this.hiddenPackLabel.value = pack.label;
      if (this.hiddenPackDisplayLabel) {
        this.hiddenPackDisplayLabel.value = this._buildDisplayLabel(pack);
      }
      if (this.hiddenPackImage) {
        const packVariant = this.findVariantForPackQuantity(pack.quantity);
        const src = packVariant?.featured_image?.src;
        const url = this._toCanonicalImageUrl(src);
        this.hiddenPackImage.value = url;
        if (this.hiddenPackImageFile) {
          this.hiddenPackImageFile.value = this._edPackImageFileKey(url);
        }
      }
    }

    _getPackById(id) {
      return this.packs.find(p => p.id === id) || null;
    }

    /**
     * Prefer URLs as Shopify serves them (usually cdn.shopify.com) — only fix
     * protocol and absolutize. Avoid rewriting path/query so Cart Transform /
     * checkout image validation keep the best chance to accept the URL.
     */
    _toCanonicalImageUrl(src) {
      if (!src) return '';

      let normalized = String(src).trim();
      if (!normalized) return '';

      if (normalized.startsWith('//')) {
        normalized = `https:${normalized}`;
      } else if (normalized.startsWith('/')) {
        normalized = `${window.location.origin}${normalized}`;
      }

      try {
        const url = new URL(normalized, window.location.origin);
        if (url.protocol === 'http:') {
          url.protocol = 'https:';
        }
        return url.toString();
      } catch (e) {
        return '';
      }
    }

    /**
     * Last path segment (filename) for checkout UI: compare to native line
     * image URL so you can skip redundant pack art when the platform already
     * shows the same asset.
     */
    _edPackImageFileKey(url) {
      if (!url) return '';
      try {
        const u = new URL(url, window.location.origin);
        const parts = u.pathname.split('/').filter(Boolean);
        const last = parts[parts.length - 1] || '';
        return decodeURIComponent(last).toLowerCase();
      } catch (e) {
        return '';
      }
    }

    /* ================================================================
     *  PRICE GUARD
     * ================================================================ */

    /**
     * Observe the price element for external mutations (theme variant price
     * changes, Seal subscription price updates). When detected, immediately
     * overwrite with our computed multipack price.
     */
    _initPriceGuard() {
      if (!this._priceEl) return;

      this._writingPrice = false;

      this._priceObserver = new MutationObserver(() => {
        if (this._writingPrice || !this._lastAppliedPack) return;
        this._writePrice(this._lastAppliedPack);
      });

      this._priceObserver.observe(this._priceEl, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    /**
     * The ONLY method that writes to price DOM elements.
     * Sets _writingPrice flag to prevent the MutationObserver from re-triggering.
     */
    _writePrice(pack) {
      this._writingPrice = true;
      this.updatePriceDisplay(pack);
      setTimeout(() => { this._writingPrice = false; }, 0);
    }

    /* ================================================================
     *  SIZE AXIS DETECTION
     * ================================================================ */

    _detectSizeAxis() {
      this.sizeAxis = null;
      this.variantPackMap = {};

      const axes = ['option1', 'option2', 'option3'];
      const multiPackQtys = this.packs
        .filter(p => p.quantity > 1)
        .map(p => p.quantity);

      if (!multiPackQtys.length) return;

      this.sizeAxis = axes.find(axis =>
        this.variants.some(v => {
          const val = v[axis];
          if (!val) return false;
          const m = String(val).match(/^(\d+)/);
          return m && multiPackQtys.includes(parseInt(m[1], 10));
        })
      ) || null;

      if (!this.sizeAxis) return;

      for (const v of this.variants) {
        const val = v[this.sizeAxis];
        if (!val) continue;
        const m = String(val).match(/^(\d+)/);
        if (m) {
          const qty = parseInt(m[1], 10);
          if (this.getPackByQuantity(qty)) {
            this.variantPackMap[v.id] = qty;
            continue;
          }
        }
        this.variantPackMap[v.id] = 1;
      }
    }

    getVariantPackQty(variant) {
      if (!variant) return null;
      const qty = this.variantPackMap[variant.id];
      return qty !== undefined ? qty : null;
    }

    findBaseVariantForFlavour(variant) {
      if (!this.variants.length || !this.sizeAxis) return null;
      const axes = ['option1', 'option2', 'option3'];
      return this.variants.find(v => {
        if (this.variantPackMap[v.id] !== 1) return false;
        return axes.every(axis => axis === this.sizeAxis || v[axis] === variant[axis]);
      }) || null;
    }

    findVariantForPackQuantity(packQty) {
      if (!this.variants.length || !this.sizeAxis) return null;
      const baseVariant = this._findVariantById(this.baseVariantId);
      if (!baseVariant) return null;
      const axes = ['option1', 'option2', 'option3'];
      return this.variants.find(v => {
        if (this.variantPackMap[v.id] !== packQty) return false;
        return axes.every(axis => axis === this.sizeAxis || v[axis] === baseVariant[axis]);
      }) || null;
    }

    resolveBaseVariant() {
      const currentVariantId = this.masterSelect ? this.masterSelect.value : null;
      const currentVariant = currentVariantId ? this._findVariantById(currentVariantId) : null;

      if (currentVariant) {
        const baseVariant = this.findBaseVariantForFlavour(currentVariant);
        if (baseVariant) {
          this.baseVariantId = String(baseVariant.id);
          this.basePrice = baseVariant.price;
          this.baseComparePrice = baseVariant.compare_at_price || 0;
          return;
        }
      }
      this.baseVariantId = this.container.dataset.baseVariantId;
      this.basePrice = parseInt(this.container.dataset.basePrice, 10);
      this.baseComparePrice = parseInt(this.container.dataset.baseComparePrice, 10);
    }

    /* ================================================================
     *  SEAL SUBSCRIPTIONS INTEGRATION (simplified)
     *
     *  With the new approach we ALWAYS submit the base variant at
     *  pack quantity. Seal naturally shows base variant's group 0
     *  subscription options. We only need to:
     *  - Translate labels
     *  - Ensure masterSelect stays on base variant at submit time
     *  - Set correct quantity at submit time
     * ================================================================ */

    initSealIntegration() {
      const tryInit = () => {
        const sealEl = this.productSection.querySelector('.sealsubs-target-element[data-product]');
        const sealContainer = this.productSection.querySelector('.sealsubs-container');
        if (!sealEl || !sealContainer) return false;
        if (!sealContainer.querySelectorAll('.sls-option-container').length) return false;
        this.sealContainer = sealContainer;
        this._onSealReady(sealEl);
        return true;
      };

      if (tryInit()) return;

      const observer = new MutationObserver(() => {
        if (tryInit()) observer.disconnect();
      });

      observer.observe(this.productSection, { childList: true, subtree: true });
    }

    _onSealReady(sealEl) {
      try { this.sealData = JSON.parse(sealEl.dataset.product); } catch (e) { return; }

      // Hide all subscription groups except group 0 (base variant subscription).
      // Groups 1-4 are for pack variants — no longer needed since discounts
      // come from the Shopify Function via _ed_pack_quantity.
      this._hideNonBaseGroups(this.sealContainer);

      // Translate labels
      this._translateSealLabels(this.sealContainer);

      // Store reference to Seal's selling_plan hidden input
      this.sellingPlanInput = this.productSection.querySelector('input[name="selling_plan"]');

      if (this._sealListenersAttached) return;
      this._sealListenersAttached = true;
    }

    /**
     * At submit time, always set masterSelect to base variant and
     * quantity to pack quantity. This ensures we never submit a
     * pack variant — the Shopify Function handles discounts via
     * _ed_pack_quantity line item property.
     *
     * For subscription multipacks, also swap the user-facing selling_plan
     * (from the Einzelprodukt group) for the pack-specific one (3er-Pack -15%
     * or 6er-Pack -20%). Cart Transform cannot price subscription lines
     * (selling-plan resolution overrides fixedPricePerUnit downstream), so the
     * multipack discount has to live on the selling plan itself.
     */
    _ensureBaseVariantAtSubmit() {
      if (this.masterSelect) {
        this.masterSelect.value = this.baseVariantId;
      }
      const pack = this._getCurrentPack();
      if (pack) {
        this._syncPackLineProperties(pack);
      }
      const targetPlanId = this._resolveSellingPlanForCurrentPack();
      if (targetPlanId && this.sellingPlanInput) {
        this.sellingPlanInput.value = targetPlanId;
      }
    }

    /**
     * Resolve the correct selling plan ID for the currently-selected pack and
     * frequency. Returns null when no subscription is active (one-time order),
     * or when the pack has no selling_plan_ids mapping configured.
     *
     * Strategy: read the currently-selected plan ID from Seal's hidden input,
     * find its interval ("month" / "2 months" / "3 months") in sealData, then
     * look up that interval in the current pack's selling_plan_ids map.
     */
    _resolveSellingPlanForCurrentPack() {
      const sellingPlanInput = this.sellingPlanInput
        || this.productSection.querySelector('input[name="selling_plan"]');
      if (!sellingPlanInput || !sellingPlanInput.value) return null;

      const pack = this._getCurrentPack();
      if (!pack || !pack.selling_plan_ids) return null;

      const selectedPlanId = String(sellingPlanInput.value);

      // EVD-177: the routine plan (window.edRoutineAllowedSellingPlanIds, set
      // only while "Routine" is active on the routine templates) is already
      // pack-specific. Never remap it by interval, or a shared interval name
      // would swap it for the pack's regular selling_plan_ids plan.
      if ((window.edRoutineAllowedSellingPlanIds || []).map(String).includes(selectedPlanId)) {
        return selectedPlanId;
      }

      let interval = null;
      for (const group of (this.sealData?.selling_plan_groups || [])) {
        for (const plan of (group.selling_plans || [])) {
          if (String(plan.id) === selectedPlanId) {
            interval = plan.options?.[0]?.value;
            break;
          }
        }
        if (interval) break;
      }
      if (!interval) return null;

      return pack.selling_plan_ids[interval] || null;
    }

    /**
     * Reset Seal to one-time mode and re-apply group hiding.
     * Called on every pack switch to ensure clean state.
     */
    _resetSealToOneTime() {
      if (!this.sealContainer) return;

      // Click the one-time radio to reset Seal internal state
      const oneTimeRadio = this.sealContainer.querySelector('input.sls-option[data-type="one_time"]');
      if (oneTimeRadio && !oneTimeRadio.checked) {
        oneTimeRadio.click();
      }

      // Also reset the custom select dropdown Seal renders
      const customSelect = this.sealContainer.querySelector(':scope > select.sls-select');
      if (customSelect) {
        const oneTimeIdx = Array.from(customSelect.options).findIndex(
          (opt, i) => i === 0 || opt.textContent.trim() === this.labelOneTime
        );
        if (oneTimeIdx !== -1) {
          customSelect.selectedIndex = oneTimeIdx;
          customSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Clear selling_plan input
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = '';
      }

      // Re-apply group hiding now and after a delay (Seal may re-render async)
      if (this.sealData) {
        this._hideNonBaseGroups(this.sealContainer);
        setTimeout(() => {
          if (this.sealContainer) this._hideNonBaseGroups(this.sealContainer);
        }, 300);
      }
    }

    /**
     * Hide all Seal subscription groups except the user-facing one (Einzelprodukt).
     *
     * With pack-specific Seal groups in play (3er-Pack -15%, 6er-Pack -20%, each
     * targeting the base variant), all three subscription containers would pass
     * the legacy "is plan allocated to base variant" filter. We instead identify
     * the user-facing group by matching plan IDs against the default pack's
     * selling_plan_ids map in multipacks.configs — that's the group whose
     * frequencies (Jeden Monat / Jeden 2. Monat / Jeden 3. Monat) the buyer
     * actually picks from. The pack-specific groups stay in the DOM but hidden;
     * their plan IDs are swapped in at submit by _resolveSellingPlanForCurrentPack.
     *
     * Falls back to the legacy base-variant-allocation filter if no pack has a
     * selling_plan_ids map (older metafields without subscription wiring).
     *
     * EVD-177 hook: assets/routine-plan-selector.js (unified-mini-abc-test-2 / -3
     * only) sets window.edRoutineAllowedSellingPlanIds to the routine pack's
     * routine_selling_plan_ids values before Seal becomes ready, so its dedicated
     * subscription group is also treated as user-facing alongside the default
     * pack's group. Empty/undefined everywhere else — zero behaviour change
     * for every other multipack product/template.
     */
    _hideNonBaseGroups(sealContainer) {
      // Preferred: match user-facing plans from default pack's selling_plan_ids
      const defaultPack = this.packs.find(p => p.default) || this.packs[0];
      const extraAllowedPlanIds = (window.edRoutineAllowedSellingPlanIds || []).map(String);
      const userFacingPlanIds = new Set([
        ...Object.values(defaultPack?.selling_plan_ids || {}).map(String),
        ...extraAllowedPlanIds
      ]);

      // Fallback for products without selling_plan_ids configured
      let basePlanIds = null;
      if (userFacingPlanIds.size === 0) {
        const baseVariant = (this.sealData.variants || [])
          .find(v => String(v.id) === this.baseVariantId);
        basePlanIds = new Set(
          (baseVariant?.selling_plan_allocations || []).map(a => String(a.selling_plan_id))
        );
      }

      const matchPlanIds = userFacingPlanIds.size > 0 ? userFacingPlanIds : basePlanIds;

      const containers = sealContainer.querySelectorAll('.sls-option-container');
      for (const c of containers) {
        const radio = c.querySelector('input.sls-option');

        // Always keep the one-time option
        if (radio?.dataset.type === 'one_time') continue;

        let isUserFacing = false;

        const freqSelect = c.querySelector('select[name="subs_interval"]');
        if (freqSelect) {
          isUserFacing = Array.from(freqSelect.options).some(opt => matchPlanIds.has(opt.value));
        }

        if (!isUserFacing) {
          const planInput = c.querySelector('input[name="selling_plan"]');
          if (planInput && matchPlanIds.has(planInput.value)) isUserFacing = true;
        }

        if (!isUserFacing && radio?.dataset.sellingPlanId) {
          if (matchPlanIds.has(radio.dataset.sellingPlanId)) isUserFacing = true;
        }

        if (!isUserFacing) {
          c.classList.add('mp-plan-hidden');
        }
      }

      // Hard guard for multipack UX: keep only one one-time group, plus one
      // subscription group per user-facing plan set. This prevents duplicate
      // "Wiederholte Lieferung" entries from pack-specific groups.
      //
      // EVD-177: when the routine allow-list (see above) is active, the
      // default pack's own group AND the routine pack's group are both
      // user-facing, so up to 2 subscription groups may stay visible instead
      // of the usual 1. Every other product/template keeps the original
      // "exactly one" behaviour.
      const maxSubscriptionGroups = extraAllowedPlanIds.length > 0 ? 2 : 1;
      let keptOneTime = false;
      let subscriptionKeptCount = 0;
      for (const c of containers) {
        if (c.classList.contains('mp-plan-hidden')) continue;
        const radio = c.querySelector('input.sls-option');
        const type = radio?.dataset.type;

        if (type === 'one_time') {
          if (!keptOneTime) {
            keptOneTime = true;
          } else {
            c.classList.add('mp-plan-hidden');
          }
          continue;
        }

        if (type === 'subscription') {
          if (subscriptionKeptCount < maxSubscriptionGroups) {
            subscriptionKeptCount++;
          } else {
            c.classList.add('mp-plan-hidden');
          }
        }
      }

      // Multipack always submits the base variant, so Seal's "variant has no
      // selling plans" reaction on pack variants (3er/6er) is a false positive.
      // Seal marks the subscription option as disabled on that variant change,
      // leaving the dropdown unclickable until the 2s polling in custom.js
      // catches up. Strip those disabled markers synchronously here so the
      // subscription option stays interactive on every pack switch.
      this._reenableSubscriptionOptions(sealContainer);

      // Prune the customSelect dropdown Seal renders on responsive layouts.
      // Seal regenerates this select from ALL .sls-option-container children
      // regardless of mp-plan-hidden, so a one-shot rebuild gets clobbered.
      // We prune by value (each option's value is its container index) and
      // attach a MutationObserver so any later Seal re-render gets re-pruned.
      this._pruneCustomSelectOptions(sealContainer);
      this._observeCustomSelectChanges(sealContainer);
    }

    /**
     * Remove customSelect options whose target container is mp-plan-hidden.
     * Seal's customSelect uses value="N" where N is the index of the matching
     * .sls-option-container child. We read each option's value, look up the
     * container, and drop the option if its container is hidden.
     */
    _pruneCustomSelectOptions(sealContainer) {
      const customSelect = sealContainer.querySelector(':scope > select.sls-select');
      if (!customSelect) return;
      const containers = Array.from(sealContainer.querySelectorAll('.sls-option-container'));
      let removed = 0;
      Array.from(customSelect.options).forEach(opt => {
        const idx = parseInt(opt.value, 10);
        const c = containers[idx];
        if (c && c.classList.contains('mp-plan-hidden')) {
          opt.remove();
          removed++;
        }
      });
      if (removed > 0 && customSelect.selectedIndex < 0 && customSelect.options.length > 0) {
        customSelect.selectedIndex = 0;
      }
    }

    /**
     * Watch for Seal-driven re-renders and re-prune.
     *
     * Observes the whole sealsubs-container with subtree so we catch both:
     * - childList changes inside the existing <select> (options added back), and
     * - replacement of the <select> element itself (Seal sometimes swaps it
     *   wholesale, which detaches any observer attached to the original <select>
     *   reference, causing later regenerations to slip through).
     *
     * Re-entrancy is safe: our own option removals trigger a mutation, the
     * observer fires once more, finds nothing to prune, and stops.
     */
    _observeCustomSelectChanges(sealContainer) {
      if (this._customSelectObserver) return;
      this._customSelectObserver = new MutationObserver(() => {
        if (this.sealContainer) this._pruneCustomSelectOptions(this.sealContainer);
      });
      this._customSelectObserver.observe(sealContainer, { childList: true, subtree: true });
    }

    /**
     * Remove Seal's "disabled" markers from visible subscription containers.
     *
     * Two distinct Seal failure modes are repaired here:
     *
     * 1. Variant-mismatch disable: when the theme switches masterSelect to a
     *    pack variant (3er/6er), Seal disables the subscription option because
     *    those variants carry no selling_plan_allocations. We always submit the
     *    base variant (see _ensureBaseVariantAtSubmit), so the buyer must still
     *    be able to choose a subscription.
     *
     * 2. Single-group degraded mode: when the Seal app has only one selling
     *    plan rule for the product, Seal sets data-only-one-option-overall="1",
     *    leaves .sls-hide on the container, and leaves the subscription
     *    container's data-selling-plan-group-id empty — which makes the
     *    toggle/frequency dropdown inert. We strip sls-hide and backfill
     *    data-selling-plan-group-id from the embedded subs_interval select.
     *
     * Scoped to containers we haven't hidden via .mp-plan-hidden.
     */
    _reenableSubscriptionOptions(sealContainer) {
      if (!sealContainer) return;

      sealContainer
        .querySelectorAll('.sls-disabled-option-container:not(.mp-plan-hidden)')
        .forEach(c => c.classList.remove('sls-disabled-option-container'));

      sealContainer
        .querySelectorAll('.sls-option-container:not(.mp-plan-hidden) select[disabled]')
        .forEach(s => s.removeAttribute('disabled'));

      sealContainer
        .querySelectorAll('.sls-option-container:not(.mp-plan-hidden) option[disabled]')
        .forEach(o => o.removeAttribute('disabled'));

      const topLevelSelect = sealContainer.querySelector(':scope > select.sls-select');
      if (topLevelSelect && topLevelSelect.hasAttribute('disabled')) {
        topLevelSelect.removeAttribute('disabled');
      }

      if (sealContainer.classList.contains('sls-hide')) {
        sealContainer.classList.remove('sls-hide');
      }

      sealContainer
        .querySelectorAll('.sls-option-container[data-selling-plan-group]:not(.mp-plan-hidden)')
        .forEach(c => {
          if (c.getAttribute('data-selling-plan-group-id')) return;
          const planSelect = c.querySelector('select[name="subs_interval"]');
          const planInput = c.querySelector('input[name="selling_plan"]');
          const planId = planSelect?.value || planInput?.value;
          if (planId) c.setAttribute('data-selling-plan-group-id', planId);
        });
    }

    _translateSealLabels(sealContainer) {
      for (const c of sealContainer.querySelectorAll('.sls-option-container')) {
        const radio = c.querySelector('input.sls-option');
        const nameLabel = c.querySelector('.sls-selling-plan-group-name');
        if (!radio || !nameLabel) continue;

        if (radio.dataset.type === 'one_time' && this.labelOneTime) {
          nameLabel.innerHTML = `<span class="text">${this.labelOneTime}</span>`;
        } else if (radio.dataset.type === 'subscription' && this.labelSubscription) {
          nameLabel.innerHTML = `<span class="text">${this.labelSubscription}</span>`;
        }
      }

      if (this.sealData?.selling_plan_groups) {
        const planNameMap = {};
        for (const group of this.sealData.selling_plan_groups) {
          for (const plan of group.selling_plans || []) {
            planNameMap[String(plan.id)] = plan.name;
          }
        }

        for (const select of sealContainer.querySelectorAll('select[name="subs_interval"]')) {
          for (const opt of select.options) {
            if (planNameMap[opt.value]) opt.textContent = planNameMap[opt.value];
          }
        }
      }
    }

    _getCurrentPack() {
      if (this._lastAppliedPack) return this._lastAppliedPack;
      const currentPackId = this.hiddenPackId ? this.hiddenPackId.value : null;
      return (currentPackId && this._getPackById(currentPackId)) || this.getDefaultPack();
    }

    /* ================================================================
     *  UI HELPERS
     * ================================================================ */

    disableCompetingQuantityInputs() {
      const quantityBlock = this.productSection.querySelector(SELECTORS.quantityBlock);
      if (!quantityBlock) return;
      const productBlock = quantityBlock.closest('.product-block');
      if (productBlock) productBlock.style.display = 'none';
      const qtyInput = quantityBlock.querySelector('input[name="quantity"]');
      if (qtyInput) qtyInput.disabled = true;
    }

    storeOriginalPiecePrice() {
      if (this._piecePriceEl) {
        this.originalPiecePriceContent = this._piecePriceEl.textContent;
      }
      if (this._piecePriceWrapper) {
        this.originalPiecePriceHidden = this._piecePriceWrapper.classList.contains('hide');
      }
    }

    getPackByQuantity(qty) {
      return this.packs.find(p => p.quantity === qty) || null;
    }

    getDefaultPack() {
      return this.packs.find(p => p.default) || this.packs[0];
    }

    /* ================================================================
     *  PACK SELECTION
     * ================================================================ */

    applyPackFromCurrentVariant() {
      const currentVariantId = this.masterSelect ? this.masterSelect.value : null;
      const currentVariant = currentVariantId ? this._findVariantById(currentVariantId) : null;

      const packQty = this.getVariantPackQty(currentVariant);
      if (packQty) {
        const pack = this.getPackByQuantity(packQty);
        if (pack) { this.applyPack(pack); return; }
      }

      const checkedInputs = this.productSection.querySelectorAll(`${SELECTORS.variantInput}:checked`);
      for (const input of checkedInputs) {
        const m = String(input.value).match(/^(\d+)/);
        if (m) {
          const pack = this.getPackByQuantity(parseInt(m[1], 10));
          if (pack) { this.applyPack(pack); return; }
        }
      }

      this.applyPack(this.getDefaultPack());
    }

    onVariantChange(evt) {
      const variant = evt.detail.variant;
      if (!variant) return;

      const baseVariantForFlavour = this.findBaseVariantForFlavour(variant);
      if (baseVariantForFlavour) {
        this.basePrice = baseVariantForFlavour.price;
        this.baseComparePrice = baseVariantForFlavour.compare_at_price || 0;
        this.baseVariantId = String(baseVariantForFlavour.id);
      }
      this.container.dataset.basePrice = this.basePrice;
      this.container.dataset.baseComparePrice = this.baseComparePrice;
      this.container.dataset.baseVariantId = this.baseVariantId;

      const packQty = this.getVariantPackQty(variant);
      const pack = packQty
        ? this.getPackByQuantity(packQty)
        : this._getPackById(this.hiddenPackId.value);

      if (pack) this.applyPack(pack);
    }

    applyPack(pack) {
      this._lastAppliedPack = pack;

      // NOTE: Do NOT set masterSelect here — it triggers theme + Seal
      // re-render cascades that break Seal state. masterSelect is set
      // to baseVariantId only at form submit time (_ensureBaseVariantAtSubmit).

      this._syncPackLineProperties(pack);

      // Reset Seal to one-time on every pack switch and re-apply group hiding
      this._resetSealToOneTime();

      // Clean URL: only rewrite legacy multipack variant links (pack qty > 1), not flavor URLs
      const url = new URL(window.location.href);
      if (url.searchParams.has('variant')) {
        const vid = url.searchParams.get('variant');
        const v = vid ? this._findVariantById(vid) : null;
        const legacyPackQty = v ? this.getVariantPackQty(v) : null;
        if (legacyPackQty && legacyPackQty > 1) {
          url.searchParams.set('variant', this.baseVariantId);
          window.history.replaceState({}, '', url.toString());
        }
      }

      // Write price — the MutationObserver handles any subsequent overwrites
      this._writePrice(pack);

      document.dispatchEvent(new CustomEvent('multipack:changed', {
        detail: {
          packId: pack.id,
          packQuantity: pack.quantity,
          discountValue: pack.discount_value || 0,
          totalPrice: this.computePackPrice(pack)
        }
      }));
    }

    /* ================================================================
     *  PRICE DISPLAY
     * ================================================================ */

    /**
     * Resolve a pack field, preferring a per-flavor override for the currently
     * selected base variant. `pack.variants` is keyed by numeric variant id
     * (matching this.baseVariantId). Falls back to the pack-level value.
     */
    _packField(pack, field) {
      const override = (pack.variants && this.baseVariantId)
        ? pack.variants[String(this.baseVariantId)]
        : null;
      const value = override ? override[field] : undefined;
      return (value === undefined || value === null) ? pack[field] : value;
    }

    computePackPrice(pack) {
      // Prefer fixed price_per_unit from metafield (in cents), per-flavor if set
      const pricePerUnit = this._packField(pack, 'price_per_unit');
      if (pricePerUnit && pricePerUnit > 0) {
        return pricePerUnit * pack.quantity;
      }

      // Legacy fallback: percentage calculation
      let total = this.basePrice * pack.quantity;
      const discountType = pack.discount_type || 'percentage';
      const discountValue = pack.discount_value || 0;

      if ((discountType === 'percentage' || discountType === 'percent') && discountValue > 0) {
        total = Math.round(total * (1 - discountValue / 100));
      }

      return total;
    }

    /**
     * Compare-at ("was" / strikethrough) price for a pack.
     * A manual compare_at_per_unit (cents per single unit) from the
     * multipacks.configs metafield takes precedence — letting merchants set
     * the crossed-out price explicitly (e.g. for sales). When absent, falls
     * back to the base variant price × quantity (previous behaviour).
     */
    computeFullPrice(pack) {
      const compareAt = this._packField(pack, 'compare_at_per_unit');
      if (compareAt && compareAt > 0) {
        return compareAt * pack.quantity;
      }
      return this.basePrice * pack.quantity;
    }

    updatePriceDisplay(pack) {
      const totalPrice = this.computePackPrice(pack);
      const { quantity } = pack;
      const fullPrice = this.computeFullPrice(pack);
      const hasDiscount = totalPrice < fullPrice;

      if (this._priceEl) {
        this._priceEl.innerHTML = theme.Currency.formatMoney(totalPrice, theme.settings.moneyFormat);
      }

      if (hasDiscount && this._comparePriceWrap && this._comparePriceEl) {
        this._comparePriceEl.innerHTML = theme.Currency.formatMoney(fullPrice, theme.settings.moneyFormat);
        this._comparePriceWrap.classList.remove('hide');
        this._priceEl?.classList.add('sale-price');
        this._priceA11y?.setAttribute('aria-hidden', 'false');
        this._comparePriceA11y?.setAttribute('aria-hidden', 'false');
      } else if (this._comparePriceWrap) {
        if (this.baseComparePrice > 0 && this.baseComparePrice > this.basePrice) {
          this._comparePriceEl.innerHTML = theme.Currency.formatMoney(this.baseComparePrice, theme.settings.moneyFormat);
          this._comparePriceWrap.classList.remove('hide');
          this._priceEl?.classList.add('sale-price');
          this._priceA11y?.setAttribute('aria-hidden', 'false');
          this._comparePriceA11y?.setAttribute('aria-hidden', 'false');
        } else {
          this._comparePriceWrap.classList.add('hide');
          this._priceEl?.classList.remove('sale-price');
          this._priceA11y?.setAttribute('aria-hidden', 'true');
          this._comparePriceA11y?.setAttribute('aria-hidden', 'true');
        }
      }

      if (this._piecePriceEl && quantity > 1) {
        const packPricePerUnit = this._packField(pack, 'price_per_unit');
        const perUnitPrice = (packPricePerUnit && packPricePerUnit > 0)
          ? packPricePerUnit
          : Math.round(totalPrice / quantity);
        this._piecePriceEl.textContent = `${theme.Currency.formatMoney(perUnitPrice, theme.settings.moneyFormat)} / Stück`;
        this._piecePriceWrapper?.classList.remove('hide');
      } else if (this._piecePriceEl && quantity <= 1) {
        if (this.originalPiecePriceContent !== undefined) {
          this._piecePriceEl.textContent = this.originalPiecePriceContent;
        }
        if (this._piecePriceWrapper) {
          this._piecePriceWrapper.classList.toggle('hide', !!this.originalPiecePriceHidden);
        }
      }
    }

    /* ================================================================
     *  LEGACY & CART CONFLICT
     * ================================================================ */

    handleLegacyVariantParam() {
      const url = new URL(window.location.href);
      const variantParam = url.searchParams.get('variant');
      if (!variantParam) return;

      const targetVariant = this._findVariantById(variantParam);
      if (!targetVariant) return;

      const packQty = this.getVariantPackQty(targetVariant);
      if (!packQty || packQty <= 1) return;

      url.searchParams.delete('variant');
      window.history.replaceState({}, '', url.toString());
    }

    /**
     * When pack is applied without a user click (e.g. ?ed_pack=), update visible pack radios
     * so Menge label, active card border, and gallery match (theme listens to change on
     * input[data-variant-input] with data-packaging-quantity).
     */
    _syncSubvariantRadioForPack(pack) {
      const qtyStr = String(pack.quantity);
      const flavourDropdown = this.productSection.querySelector('[data-flavour-dropdown]');
      const flavour = flavourDropdown ? flavourDropdown.value : null;

      let inputs = Array.from(
        this.productSection.querySelectorAll('input[data-variant-input]')
      ).filter(inp => String(inp.getAttribute('data-packaging-quantity') || '') === qtyStr);

      if (!inputs.length) return;

      if (flavour) {
        const byFlavour = inputs.filter(
          inp => (inp.getAttribute('data-variant-flavour') || '') === flavour
        );
        if (byFlavour.length) inputs = byFlavour;
      }

      const input =
        inputs.find(inp => !inp.classList.contains('disabled') && !inp.disabled) || inputs[0];

      if (!input) return;

      if (!input.checked) {
        input.checked = true;
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Deep-link pack tier from URL (feeds / marketing).
     * ?ed_pack=<pack id> | ?pack_qty=<quantity> — see docs/multipack-pdp-deep-link-implementation.md
     */
    handlePackQueryParam() {
      const url = new URL(window.location.href);
      const edPackId = url.searchParams.get('ed_pack');
      const packQtyRaw = url.searchParams.get('pack_qty');

      if (!edPackId && (packQtyRaw === null || packQtyRaw === '')) return;

      let pack = null;
      if (edPackId) {
        pack = this._getPackById(edPackId);
      }
      if (!pack && packQtyRaw !== null && packQtyRaw !== '') {
        const n = parseInt(packQtyRaw, 10);
        if (!isNaN(n) && n > 0) {
          pack = this.getPackByQuantity(n);
        }
      }
      if (!pack) return;

      this.applyPack(pack);
      this._syncSubvariantRadioForPack(pack);

      // Remove only our marketing params. Theme.Variants._updateHistoryState may set
      // ?variant=<id> for the selected line — keep it so refresh/share preserve selection
      // and behaviour stays aligned with non-multipack variant URLs.
      // Defer so this runs after any synchronous history writes from the radio change.
      queueMicrotask(() => {
        try {
          const u = new URL(window.location.href);
          if (!u.searchParams.has('ed_pack') && !u.searchParams.has('pack_qty')) return;
          u.searchParams.delete('ed_pack');
          u.searchParams.delete('pack_qty');
          const qs = u.searchParams.toString();
          window.history.replaceState({}, '', u.pathname + (qs ? '?' + qs : '') + u.hash);
        } catch (e) {
          /* noop */
        }
      });
    }

    checkCartConflicts() {
      const productId = this.container.dataset.productId;

      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          const conflictKeys = cart.items
            .filter(item =>
              String(item.product_id) === productId &&
              !(item.properties && item.properties._ed_pack_quantity)
            )
            .map(item => item.key);

          if (!conflictKeys.length || !theme.strings.multipackCartConflictWarning) return;

          const atcBtn = this.productSection.querySelector('[data-add-to-cart]');
          if (atcBtn) {
            atcBtn.disabled = true;
            atcBtn.classList.add('btn--disabled');
          }

          const warning = document.createElement('div');
          warning.className = 'multipack-selector__cart-warning';

          const msg = document.createElement('span');
          msg.textContent = theme.strings.multipackCartConflictWarning;
          warning.appendChild(msg);

          const removeLink = document.createElement('a');
          removeLink.href = '#';
          removeLink.className = 'multipack-selector__cart-warning-remove';
          removeLink.textContent = theme.strings.multipackCartConflictRemove || 'Remove';
          removeLink.addEventListener('click', e => {
            e.preventDefault();
            removeLink.classList.add('is-loading');

            const updates = {};
            conflictKeys.forEach(key => { updates[key] = 0; });
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            })
            .then(r => r.json())
            .then(updatedCart => {
              if (atcBtn) {
                atcBtn.disabled = false;
                atcBtn.classList.remove('btn--disabled');
              }
              warning.remove();
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
              document.dispatchEvent(new CustomEvent('cart:build'));
            })
            .catch(() => {
              removeLink.classList.remove('is-loading');
            });
          });
          warning.appendChild(removeLink);

          const variantPicker = this.productSection.querySelector('.variant-wrapper');
          if (variantPicker) {
            variantPicker.parentNode.insertBefore(warning, variantPicker);
          }
        })
        .catch(() => { /* silently fail */ });
    }
  }

  /* ================================================================
   *  BOOTSTRAP
   * ================================================================ */

  function init() {
    document.querySelectorAll(SELECTORS.container).forEach(container => {
      new MultipackSelector(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
